import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDisclosure } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import GenericFormField from '../common/form/GenericFormField';
import SelectComponent from '../common/form/SelectComponent';
import CheckProductModal from '../common/modal/CheckProductModal';
import useUserProfile from '@/hooks/useUserProfile';
import { useUseCases } from "@/context/UseCasesProvider";
import { Control } from 'react-hook-form';
import useConfigFieldsValues from '@/hooks/useConfigFieldsValues';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { formatAddressDisplay } from '../common/modal/AddressModal';

interface CustomerInfoTabProps {
  dataForm: any;
  methods: {
    control: Control<any>;
    setValue: (name: string, value: any) => void;
    watch: (name: string | string[]) => any;
  };
  setErrors: React.Dispatch<React.SetStateAction<any[]>>;
  classNames: Record<string, string>;
  selectedCustomer: any;
  currentErrors: any[];
}
interface ExtractionField {
  title: string;
  text: string;
  key: string;
  type: "string" | "address" | "date" | "price"| "select";
  choice?: string[];
  defaultValue? : string;
  isErasable: boolean;
  isMandatory: boolean;
  isVisible: boolean;
  isExportable: boolean;
}

const CustomerInfoTab: React.FC<CustomerInfoTabProps> = React.memo(({
  dataForm,
  methods,
  setErrors,
  classNames,
  selectedCustomer,
  currentErrors,
}) => {
  const { t } = useTranslation();
  const { extractionFields, checksOrderMetadata, loading, error } = useConfigFieldsValues();
  const [customerID, setCustomerID] = useState<string>(() => {
    if (typeof selectedCustomer === 'object' && selectedCustomer?.value) {
      return selectedCustomer.value;
    }
    if (typeof selectedCustomer === 'string' && selectedCustomer) {
      return selectedCustomer;
    }
    return dataForm?.order?.metadata?.customer_id?.value || '';
  });

  const customerChanged = methods.watch("_customerChanged") || false;

  const userModifiedFieldsValue = methods.watch("_userModifiedFields") || [];
  const userModifiedFields = useMemo(() => new Set<string>(userModifiedFieldsValue as string[]), [userModifiedFieldsValue]);

  const setUserModifiedFields = useCallback((updater: (prev: Set<string>) => Set<string>) => {
    const currentSet = new Set<string>((methods.watch("_userModifiedFields") as string[]) || []);
    const newSet = updater(currentSet);
    methods.setValue("_userModifiedFields", Array.from(newSet));
  }, [methods]);

  const deliveryAddressValue = methods.watch("delivery_address");
  const billingAddressValue = methods.watch("billing_address");
  const senderPostalAddressValue = methods.watch("sender_postal_address");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isDataReadOnlyOperators } = useUserProfile();
  const settingsInterface = useUseCases();
  const reprocessActive = settingsInterface?.settings?.processing?.reprocess_onChange_customer;

  const isAddressInSelectMode = useCallback((addressType: string): boolean => {
    const selectModeMap: Record<string, boolean> = {
      "delivery_address": checksOrderMetadata.delivery_address_matching?.value ?? false,
      "billing_address": checksOrderMetadata.billing_address_matching?.value ?? false,
      "sender_postal_address": checksOrderMetadata.sender_address_matching?.value ?? false
    };
    return selectModeMap[addressType] || false;
  }, [checksOrderMetadata]);

  const setAddressInSelectMode = useCallback((addressType: string, addressMetadata: any) => {
    if (addressMetadata?.value !== undefined) {
      methods.setValue(addressType, addressMetadata.value);
    }
  }, [methods]);

  const setAddressWithDetails = useCallback((addressType: string, addressMetadata: any) => {
    if (addressMetadata?.valueDetail !== undefined && addressMetadata?.valueDetail !== null) {
      const formattedAddress = formatAddressDisplay(addressMetadata.valueDetail);

      methods.setValue(addressType, addressMetadata.value || formattedAddress);
      methods.setValue(`${addressType}_details`, {
        value: addressMetadata.value || formattedAddress,
        label: formattedAddress,
        valueDetail: addressMetadata.valueDetail
      });
    }
  }, [methods]);

  const processAddressField = useCallback((
    addressType: string,
    addressMetadata: any,
    fieldConfig: ExtractionField | undefined
  ) => {
    const isSelectMode = isAddressInSelectMode(addressType);

    if (isSelectMode) {
      setAddressInSelectMode(addressType, addressMetadata);
      return;
    }

    if (fieldConfig?.type === "address") {
      setAddressWithDetails(addressType, addressMetadata);
      return;
    }

    if (addressMetadata?.value !== undefined) {
      methods.setValue(addressType, addressMetadata.value);
    }
  }, [methods, isAddressInSelectMode, setAddressInSelectMode, setAddressWithDetails]);

  useEffect(() => {
    const addressMapping = {
      "delivery_address": deliveryAddressValue,
      "billing_address": billingAddressValue,
      "sender_postal_address": senderPostalAddressValue
    };

    for (const [addressType, currentValue] of Object.entries(addressMapping)) {
      if (userModifiedFields.has(addressType) || currentValue) {
        continue;
      }

      const addressMetadata = dataForm?.order?.metadata[addressType];
      const fieldConfig = extractionFields?.find((field: ExtractionField) => field.key === addressType) as ExtractionField | undefined;

      processAddressField(addressType, addressMetadata, fieldConfig);
    }
  }, [dataForm, deliveryAddressValue, billingAddressValue, senderPostalAddressValue, userModifiedFields, extractionFields, processAddressField]);


  useEffect(() => {
    if (userModifiedFields.has('customer_id')) {
      return;
    }

    const actualCustomerId = dataForm?.order?.metadata?.customer_id?.value;

    if (selectedCustomer && !methods.watch("selectcustomer")) {
      methods.setValue("selectcustomer", selectedCustomer);

      if (typeof selectedCustomer === 'object' && 'value' in selectedCustomer) {
        setCustomerID(selectedCustomer.value || '');
      } else if (typeof selectedCustomer === 'string') {
        setCustomerID(selectedCustomer);
      }
    } else if (actualCustomerId && actualCustomerId !== 'None') {
      setCustomerID(actualCustomerId);
    }
  }, [selectedCustomer, methods, dataForm, userModifiedFields]);

  const handleFieldError = useCallback((fieldKey: string, isCorrected: boolean) => {
    setErrors((prevErrors) => {
      const elementIndex = prevErrors.findIndex(
        (error: any) => error.index === fieldKey,
      );
      const newFieldError = {
        index: fieldKey,
        [`${fieldKey}Corrected`]: isCorrected,
      };
      // manage error if not available before customer change
      const updatedErrors = [...prevErrors];
      if (elementIndex === -1) {
        updatedErrors.push(newFieldError);
      } else {
        updatedErrors[elementIndex] = newFieldError;
      }
      return updatedErrors;
    });
  }, [setErrors]);

  const handleFieldValidate = useCallback((fieldKey: string) => {
    methods.setValue(`${fieldKey}_validated`, true);
    handleFieldError(fieldKey, true);
  }, [methods, handleFieldError]);

  const handleCheckProductsByCustomers = useCallback((newValue: any) => {
    setCustomerID(newValue.value);
    methods.setValue('customer_id_validated', false);
    methods.setValue('_customerChanged', true);

    setUserModifiedFields(prev => new Set(prev).add('customer_id'));

    const addressFieldKeys = ["delivery_address", "billing_address", "sender_postal_address"];
    const addressFields = extractionFields?.filter((field: ExtractionField) => field.isVisible && addressFieldKeys.includes(field.key)) ?? [];
    for (const field of addressFields) {
      methods.setValue(field.key, "");
      methods.setValue(`${field.key}_details`, {
        value: "",
        label: "",
        valueDetail: undefined
      });
      methods.setValue(`${field.key}_validated`, false);

      if (dataForm?.order?.metadata?.[field.key]) {
        dataForm.order.metadata[field.key].value = "";
        dataForm.order.metadata[field.key].valueConfidence = 0;
      }

      if (field.isMandatory) {
        handleFieldError(field.key, false);
      }
    }

    setUserModifiedFields(prev => {
      const newSet = new Set(prev);
      for (const key of addressFieldKeys) {
        newSet.delete(key);
      }
      return newSet;
    });

    if (reprocessActive?.value) {
      onOpen();
    }
  }, [onOpen, reprocessActive, methods, dataForm, extractionFields, handleFieldError]);


  const handleUpdateAddress = useCallback((newValue: any, fieldKey: string) => {
    console.log(`Mise à jour de ${fieldKey} avec`, newValue);

    methods.setValue(fieldKey, newValue.value);

    methods.setValue(`${fieldKey}_details`, {
      value: newValue.value,
      label: newValue.label,
      valueDetail: newValue.valueDetail
    });

    methods.setValue(`${fieldKey}_validated`, false);

    setUserModifiedFields(prev => new Set(prev).add(fieldKey));
  }, [methods]);

  const correctedFieldsMap = useMemo(() => {
    const map = new Map();
    if (currentErrors) {
      for (const error of currentErrors) {
        for (const key in error) {
          if (key.endsWith('Corrected') && error[key] === true) {
            map.set(key.replace('Corrected', ''), true);
          }
        }
      }
    }
    return map;
  }, [currentErrors]);
  
  const isFieldCorrected = (fieldName) => {
    return correctedFieldsMap.has(fieldName);
  };

  const renderCustomerIdField = useCallback((field: ExtractionField, valueConfidence: number) => {
    const isAlreadyValidated = valueConfidence >= 75;
    const isFieldValidated = methods.watch('customer_id_validated') || false;
    const isDisabled = customerChanged || isAlreadyValidated || isFieldValidated;

    return (
      <div key={field.key} className="w-full">
        <div className="text-[10px] uppercase text-vistagrey/80 mt-3">
          <div className="pointer-events-none flex items-center">{t("customer_name")}</div>
        </div>

        <div className="flex items-end">
          <div className="w-full">
            <SelectComponent
              name="selectcustomer"
              control={methods.control}
              classNames={classNames}
              apiUrl="customers"
              endpoint="customers"
              onSelectionChange={(newValue: any) => {
                handleFieldError("customer_id", true);
                handleCheckProductsByCustomers(newValue);
              }}
              isRequired
              defaultValue={customerID}
              valueConfidence={valueConfidence}
              isModified={isFieldCorrected('customer_id')}
            />
          </div>
          <button
            className={`flex items-center p-2 rounded-full text-white mx-1 ${
              isDisabled
                ? 'bg-vistagrey/10 cursor-not-allowed opacity-50'
                : 'bg-vistagreen/10 cursor-pointer'
            }`}
            onClick={(event) => {
              event.preventDefault();
              if (!isDisabled) {
                handleFieldValidate("customer_id");
              }
            }}
            disabled={isDisabled}
          >
            <IoIosCheckmarkCircle className={isDisabled ? "fill-vistagrey/50 h-6 w-6" : "fill-vistagreen/70 h-6 w-6"} />
            <span className="w-0 group-hover/validate:w-auto group-hover/validate:px-2 group-hover/validate:flex hidden">
              {t("validate")}
            </span>
          </button>
        </div>
      </div>
    );
  }, [methods.control, classNames, customerID, customerChanged, isFieldCorrected, t, handleFieldError, handleCheckProductsByCustomers, handleFieldValidate]);

  const renderAddressField = useCallback((field: ExtractionField, fieldData: any, defaultAddressValue: any, valueConfidence: number) => {
    const isAlreadyValidated = valueConfidence >= 75;
    const isFieldValidated = methods.watch(`${field.key}_validated`) || false;
    const isDisabled = customerChanged || isAlreadyValidated || isFieldValidated;

    return (
      <div key={field.key} className="w-full pl-10">
        <div className="text-[10px] uppercase text-vistagrey/80 mt-3">
          <div className="pointer-events-none flex items-center">{t(field.key)}</div>
        </div>

        <div className="flex items-end">
          <div className="w-full">
            <SelectComponent
              name={field.key}
              control={methods.control}
              classNames={classNames}
              apiUrl={field.key}
              endpoint={field.key}
              onSelectionChange={(newValue: any) => {
                handleFieldError(field.key, true);
                handleUpdateAddress(newValue, field.key);
              }}
              isRequired={field.isMandatory}
              defaultValue={defaultAddressValue}
              customerId={customerID}
              valueConfidence={valueConfidence}
              fieldConfig={field}
              isModified={isFieldCorrected(field.key)}
            />
          </div>
          <button
            className={`flex items-center p-2 rounded-full text-white mx-1 ${
              isDisabled
                ? 'bg-vistagrey/10 cursor-not-allowed opacity-50'
                : 'bg-vistagreen/10 cursor-pointer'
            }`}
            onClick={(event) => {
              event.preventDefault();
              if (!isDisabled) {
                handleFieldValidate(field.key);
              }
            }}
            disabled={isDisabled}
          >
            <IoIosCheckmarkCircle className={isDisabled ? "fill-vistagrey/50 h-6 w-6" : "fill-vistagreen/70 h-6 w-6"} />
            <span className="w-0 group-hover/validate:w-auto group-hover/validate:px-2 group-hover/validate:flex hidden">
              {t("validate")}
            </span>
          </button>
        </div>
      </div>
    );
  }, [methods.control, classNames, customerID, customerChanged, isFieldCorrected, t, handleFieldError, handleUpdateAddress, handleFieldValidate]);

  const renderGenericField = useCallback((field: ExtractionField, valueConfidence: number, fieldData: any) => {
    let initialValue: any = "";
    let initialAddressData = null;

    if (fieldData?.value !== null && fieldData?.value !== undefined && fieldData?.value !== "") {
      initialValue = fieldData.value;
    } else if (field.defaultValue !== null && field.defaultValue !== undefined && field.defaultValue !== "") {
      initialValue = field.defaultValue;
    }

    if (field.type === "address" && fieldData?.valueDetail) {
      initialAddressData = fieldData.valueDetail;
    }

    const isFieldValidated = methods.watch(`${field.key}_validated`) || false;

    return (
      <div key={field.key} className={field.type === "price" ? "w-1/4" : "w-full"}>
        <GenericFormField
          control={methods.control}
          classNames={classNames}
          isReadOnly={isDataReadOnlyOperators}
          t={t}
          valueConfidence={valueConfidence}
          onFieldChange={() => {
            handleFieldError(field.key, true);
            setUserModifiedFields(prev => new Set(prev).add(field.key));
          }}
          onFieldValidate={() => handleFieldValidate(field.key)}
          fieldConfig={field}
          initialValue={initialValue}
          setValue={methods.setValue}
          initialAddressData={initialAddressData}
          fieldData={fieldData}
          isFieldValidated={isFieldValidated}
          isModified={userModifiedFields.has(field.key)}
        />
      </div>
    );
  }, [methods, classNames, isDataReadOnlyOperators, t, handleFieldError, handleFieldValidate, setUserModifiedFields, userModifiedFields]);

  const shouldRenderAddressField = useCallback((field: ExtractionField) => {
    if (field.key === "delivery_address" && checksOrderMetadata.delivery_address_matching?.value) return true;
    if (field.key === "billing_address" && checksOrderMetadata.billing_address_matching?.value) return true;
    if (field.key === "sender_postal_address" && checksOrderMetadata.sender_address_matching?.value) return true;
    return false;
  }, [checksOrderMetadata]);

  const renderFormFields = useCallback(() => {
    if (loading) return <p>{t('loading')}</p>;
    if (error) return <p>{t('error_loading_data', { message: error.message })}</p>;
    return (
      <div className="flex flex-wrap gap-2">
        {extractionFields?.filter((field: ExtractionField) => field.isVisible).map((field: ExtractionField) => {
          const fieldData = dataForm?.order.metadata[field.key];
          let valueConfidence = fieldData?.valueConfidence;
          if (field.isMandatory) {
            valueConfidence ??= 0;
          }

          if (field.key === "customer_id") {
            return renderCustomerIdField(field, valueConfidence);
          }

          if (shouldRenderAddressField(field)) {
            let addressValue;
            if (field.key === "delivery_address") {
              addressValue = deliveryAddressValue;
            } else if (field.key === "billing_address") {
              addressValue = billingAddressValue;
            } else {
              addressValue = senderPostalAddressValue;
            }
            const defaultAddressValue = addressValue ?? (fieldData?.value === null ? undefined : fieldData.value);
            return renderAddressField(field, fieldData, defaultAddressValue, valueConfidence);
          }

          return renderGenericField(field, valueConfidence, fieldData);
        })}
      </div>
    );
  }, [loading, error, extractionFields, dataForm, deliveryAddressValue, billingAddressValue, senderPostalAddressValue, renderCustomerIdField, shouldRenderAddressField, renderAddressField, renderGenericField]);

  return (
    <div className="bg-white rounded-xl">
      <div className="border-b-1 border-vistagrey/30 px-3 pt-1 pb-3 text-xs font-bold">
        {t('customer_details')}
      </div>
      <div className="p-3">{renderFormFields()}</div>
      <CheckProductModal
        isOpen={isOpen}
        onClose={onClose}
        orderId={dataForm?.process?.jobID}
        newSelectedCustomer={customerID}
      />
    </div>
  );
});

export default CustomerInfoTab;